// Importing necessary react hooks
import { useState, useEffect } from "react";
// Importing necessary hooks for routing
import { useLocation, useNavigate } from "react-router-dom";
// Importing Material-UI components
import {
  styled,
  useTheme,
  Box,
  List,
  CssBaseline,
  Divider,
  IconButton,
  ListItem,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
// Custom hook for authentication info
import { useAuthInfo } from "../../common";
// Logo components
import { SidebarLogoShort } from "./SidebarLogoShort.component";
import { SidebarLogoLong } from "./SidebarLogoLong.component";
// Custom list item component
import { CustomListItem } from "./CustomListItem.component";
// Menu items data
import { menuItems } from "../menuItems";
// Logout button component
import { SidebarLogoutButton } from "./SidebarLogoutButton.component";

// Width of the sidebar drawer
const drawerWidth = 300;

// Function to define styles when the drawer is open
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

// Function to define styles when the drawer is closed
const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  // Adjust width when closed
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

// Drawer header styled component
const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

// Styled Drawer component with dynamic styles based on open state
const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  // flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

// Main Sidebar component
export function Sidebar({ children }) {
  // Get current location
  const location = useLocation();
  // Extract pathname from location
  const pathname = location.pathname;
  // Hook to navigate programmatically
  const history = useNavigate();

  const theme = useTheme();
  // State to manage drawer open/close
  const [open, setOpen] = useState(false);
  const decoded = useAuthInfo();
  // State for selected menu item
  const [isSelected, setIsSelected] = useState("");

  // Function to open the drawer
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  // Function to close the drawer
  const handleDrawerClose = () => {
    setOpen(false);
  };

  // Effect to update selected item when pathname changes
  useEffect(() => {
    setIsSelected(pathname);
  }, [pathname]);

  // Effect to handle routing for specific pages based on user role
  useEffect(() => {
    switch (decoded.role) {
      // No action needed for procurement role
      case "procurement":
        //make the pattern case insensitive
        var requestDetails = new RegExp("requestDetails", "gi");
        const newPath = pathname.replace(requestDetails, "procurementApproval");
        history(newPath);
        break;
      // Redirect if user is not authorized for procurement approval
      default:
        if (
          pathname.toLowerCase().includes("procurementApproval".toLowerCase())
        ) {
          //make the pattern case insensitive
          var procurementApproval = new RegExp("procurementApproval", "gi");
          const newPath = pathname.replace(
            procurementApproval,
            "requestDetails"
          );
          history(newPath);
        }
        break;
    }
  }, [pathname, decoded.role]);

  return (
    <Box
      sx={{
        display: "flex", // Flexbox layout for the sidebar and main content
        maxWidth: "100vw", // Prevent overflow
      }}
    >
      <CssBaseline />

      <Drawer
        variant="permanent"
        open={open}
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          alignItems: "center",
          "& .MuiDrawer-paper": {
            height: "auto",
            bottom: 0,
            boxShadow: 0,
            border: 0,
            backgroundColor: "#fefefe",
            backgroundImage:
              "linear-gradient(to bottom, #fff 40%, rgba(231, 231, 231) 10%)",
            backgroundPosition: "right",
            backgroundSize: "1px 10px",
            backgroundRepeat: "repeat-y",
          },
        }}
      >
        <DrawerHeader>
          {open ? (
            // Close button when drawer is open
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === "rtl" ? (
                <ChevronRightIcon />
              ) : (
                <ChevronLeftIcon />
              )}
            </IconButton>
          ) : (
            // Open button when drawer is closed
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                ...(open && { display: "none" }),
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </DrawerHeader>
        <Divider />
        <List>
          <ListItem sx={{ py: "25px" }}>
            {open ? (
              // Show long logo when drawer is open
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <SidebarLogoLong />
              </Box>
            ) : (
              // Show short logo when drawer is closed
              <SidebarLogoShort />
            )}
          </ListItem>
          {/* Render menu items based on user access */}
          {menuItems.map((menuItem) =>
            menuItem.access.map(
              (access, index) =>
                access === decoded.role && (
                  <CustomListItem
                    menuItem={menuItem}
                    isSelected={isSelected}
                    open={open}
                    key={index}
                  />
                )
            )
          )}
          <SidebarLogoutButton open={open} />
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: 3,
          transition: "max-width 0.3s",
          maxWidth: {
            xs: "100vw",
            md: open ? "75vw" : "92vw",
            lg: open ? "80vw" : "95vw",
          },
        }}
      >
        {/*  Header for main content area */}
        <DrawerHeader />
        {/* Render children components */}
        {children}
      </Box>
    </Box>
  );
}
